import React, { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { NavigationProp } from "@react-navigation/native";
import { ProfileTabs } from "@modules/profile";
import { ProfileMenuTab } from "@atomic";
import { Colors, Spacing } from "@constants";
import { I18n } from "react-redux-i18n";
import {
  CarouselArrowLeft,
  CarouselArrowRight,
  DisconnectBlack,
  HelpBlack,
  LegalInfoBlack,
  OrdersSVG,
  ProfileBlack,
  SettingsBlack,
} from "@assets";
import { TestIDs } from "@utils";
import { AuthActions } from "@modules";
import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "@redux";
import { useDevices } from "@hooks";
interface IProps {
  selectedTab: ProfileTabs;
  callback: (tab: ProfileTabs) => void;
  navigation: NavigationProp<any>;
  showLeftContainer: boolean;
}

export const ProfileMenu: React.FC<IProps> = React.memo(
  ({ callback, selectedTab, navigation, showLeftContainer }) => {
    const dispatch = useDispatch();
    const [isMobile, /*isSmallTablet*/, /*isMediumTablet*/, isLargeTablet] = useDevices();
    const [closeMenu, setCloseMenu] = useState(false);

    const styles = useMemo(() => _styles(isMobile, showLeftContainer, closeMenu), [
      isMobile,
      showLeftContainer,
      closeMenu
    ]);

    useEffect(() => {
      if (isLargeTablet) {
        setCloseMenu(true);
      }
    }, [isLargeTablet]);

    const onSelectedTab = (tab: ProfileTabs) => {
      callback(tab);
    };

    const isTermsAccepted = useSelector(
      (state: GlobalState) => state.auth?.userInfo?.isTermsAccepted
    );

    const handleNeedHelp = () => {
      window.open(window.config.ZENDESK_BASE_URL, "_blank")?.focus();
    };

    const handleLogout = async () => {
      dispatch(AuthActions.setLogoutUser(true));
    };

    return (
      <View testID={TestIDs.profile.views.profileMenu} style={styles.container}>
        <Pressable style={styles.openCloseMenuContainer} onPress={() => {
          setCloseMenu((prevState) => !prevState);
        }}>
          {closeMenu ? <CarouselArrowRight size={20}/> : <CarouselArrowLeft size={20}/>}
        </Pressable>
        <ProfileMenuTab
          tabName={ProfileTabs.ACCOUNT}
          title={I18n.t("profile.myAccount")}
          icon={<ProfileBlack />}
          isSelected={selectedTab === ProfileTabs.ACCOUNT}
          onSelectedTab={onSelectedTab}
          forwardTestID={TestIDs.profile.actions.myAccountTab}
          showForward
          collapsed={closeMenu && !isMobile}
        />
        <View style={styles.separator} />
        <ProfileMenuTab
          tabName={ProfileTabs.ORDERS}
          title={I18n.t("profile.myOrders")}
          isDisabled={!isTermsAccepted}
          icon={<OrdersSVG />}
          isSelected={selectedTab === ProfileTabs.ORDERS}
          onSelectedTab={onSelectedTab}
          forwardTestID={TestIDs.profile.actions.myOrdersTab}
          showForward
          collapsed={closeMenu && !isMobile}
        />
        <View style={styles.separator} />
        <ProfileMenuTab
          tabName={ProfileTabs.SETTINGS}
          title={I18n.t("profile.settings")}
          icon={<SettingsBlack />}
          isSelected={selectedTab === ProfileTabs.SETTINGS}
          onSelectedTab={onSelectedTab}
          forwardTestID={TestIDs.profile.actions.settingTab}
          showForward
          collapsed={closeMenu && !isMobile}
        />
        <View style={styles.separator} />
        <ProfileMenuTab
          tabName={ProfileTabs.LEGAL_INFORMATION}
          title={I18n.t("profile.legalInformation")}
          icon={<LegalInfoBlack />}
          isSelected={selectedTab === ProfileTabs.LEGAL_INFORMATION}
          onSelectedTab={onSelectedTab}
          forwardTestID={TestIDs.profile.actions.legalInfosTab}
          showForward
          collapsed={closeMenu && !isMobile}
        />
        <View style={styles.separator} />
        <ProfileMenuTab
          tabName={ProfileTabs.HELP}
          title={I18n.t("profile.help")}
          icon={<HelpBlack />}
          isSelected={selectedTab === ProfileTabs.HELP}
          onSelectedTab={handleNeedHelp}
          forwardTestID={TestIDs.profile.actions.helpTab}
          showForward
          collapsed={closeMenu && !isMobile}
        />
        <View style={styles.separator} />
        <ProfileMenuTab
          tabName={ProfileTabs.LOGOUT}
          title={I18n.t("profile.logout")}
          icon={<DisconnectBlack />}
          isSelected={selectedTab === ProfileTabs.LOGOUT}
          onSelectedTab={handleLogout}
          forwardTestID={TestIDs.profile.actions.logoutTab}
          collapsed={closeMenu && !isMobile}
        />
      </View>
    );
  }
);

const _styles = (isMobile: boolean, showLeftContainer: boolean, closeMenu: boolean) =>
  StyleSheet.create({
    container: {
      justifyContent: "flex-start",
      alignItems: "flex-start",
      minWidth: closeMenu ? 'inherit' : 284,
      display: showLeftContainer ? "flex" : "none",
    },
    separator: {
      width: isMobile ? "90vw" : "100%",
      height: 1,
      backgroundColor: Colors.disabledBackground,
      marginVertical: Spacing.L,
    },
    openCloseMenuContainer: {
      marginBottom: 20
    }
  });
